import DiseaseEncyclopediaModel from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { createDiseaseEncyclopediaDetailUrl } from '@/entities/disease-encyclopedia/util';
import useGetDiagnosisCategories from '@/hooks/telemedicine/useGetDiagnosisCategories';
import useCustomRouter from '@/hooks/useCustomRouter';
import { DepartmentCategoryModel } from '@/models/diagnosis/category';
import { Client } from '@/types/app';
import { ROUTES } from '@/types/common';
import { useState } from 'react';
import useGetPopularDiseaseEncyclopedias from './useGetPopularDiseaseEncyclopedias';

const MOBILE_DEPARTMENT_CATEGORIES_LIMIT = 7;
const DESKTOP_DEPARTMENT_CATEGORIES_LIMIT = 13;

export interface DiseaseEncyclopediaViewModel {
  departmentCategories: DepartmentCategoryModel[];
  popularDiseaseEncyclopedia: DiseaseEncyclopediaModel[] | undefined;
  keyword: string;
  onClickDiseaseEncyclopedia: (departmentCategory: DepartmentCategoryModel) => () => void;
  handleSubmit: () => void;
  onClickShowAllDepartment: () => void;
  handleResetSearchKeyword: () => void;
  handleChangeSearchKeyword: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClickPopularDisease: (disease: DiseaseEncyclopediaModel) => () => void;
  onClickShowAllDiseases: () => void;
}

interface Props {
  client: Client;
}

const useDiseaseEncyclopediaViewModel = (props: Props): DiseaseEncyclopediaViewModel => {
  const { router } = useCustomRouter();
  const [keyword, setKeyword] = useState<string>('');
  const handleSubmit = () => {
    const searchKeyword = keyword.trim();
    if (searchKeyword) {
      router.replace({
        pathname: ROUTES.DISEASE_ENCYCLOPEDIA.SEARCH.path,
        query: { keyword: searchKeyword },
      });
    }
  };

  const handleResetSearchKeyword = () => {
    setKeyword('');
  };

  const { categoriesInfo } = useGetDiagnosisCategories();
  const departmentCategories = [
    ...categoriesInfo.getDepartmentCategories().sort((prev, next) => prev.name.localeCompare(next.name)),
  ].slice(0, props.client.isMobile ? MOBILE_DEPARTMENT_CATEGORIES_LIMIT : DESKTOP_DEPARTMENT_CATEGORIES_LIMIT);

  const { popularDiseaseEncyclopedia } = useGetPopularDiseaseEncyclopedias();

  const onClickDiseaseEncyclopedia = (departmentCategory: DepartmentCategoryModel) => () => {
    router.push(`${ROUTES.DISEASE_ENCYCLOPEDIA.SEARCH.path}?keyword=${departmentCategory.name}`);
  };

  const handleChangeSearchKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const onClickShowAllDepartment = () => {
    router.push(ROUTES.DISEASE_ENCYCLOPEDIA.SELECT_SUBJECT.path);
  };

  const handleClickPopularDisease = (disease: DiseaseEncyclopediaModel) => () => {
    router.push(`${createDiseaseEncyclopediaDetailUrl(disease)}`);
  };

  const onClickShowAllDiseases = () => {
    router.push(ROUTES.DISEASE_ENCYCLOPEDIA.TOTAL.path);
  };

  return {
    departmentCategories,
    keyword,
    popularDiseaseEncyclopedia,
    handleResetSearchKeyword,
    handleSubmit,
    onClickDiseaseEncyclopedia,
    handleChangeSearchKeyword,
    onClickShowAllDepartment,
    handleClickPopularDisease,
    onClickShowAllDiseases,
  };
};

export default useDiseaseEncyclopediaViewModel;
